import axios from "axios";
import { getAccessToken } from "./authStorage";

/**
 * Generates a semi-stable device fingerprint using browser attributes.
 * First Principle: Identity should be bound to the hardware/software profile to prevent session hijacking.
 */
const getDeviceFingerprint = () => {
  try {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const txt = "pixelmoon-fingerprint-v1";
    ctx.textBaseline = "top";
    ctx.font = "14px 'Arial'";
    ctx.textBaseline = "alphabetic";
    ctx.fillStyle = "#f60";
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = "#069";
    ctx.fillText(txt, 2, 15);
    ctx.fillStyle = "rgba(102, 204, 0, 0.7)";
    ctx.fillText(txt, 4, 17);
    const canvasData = canvas.toDataURL();

    const data = [
      navigator.userAgent,
      navigator.language,
      screen.colorDepth,
      screen.width + "x" + screen.height,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      !!window.indexedDB,
      typeof navigator.cpuClass,
      navigator.platform,
      navigator.doNotTrack,
      canvasData,
    ].join("###");

    // Simple hash function (cyrb53)
    let h1 = 0xdeadbeef,
      h2 = 0x41c6ce57;
    for (let i = 0, ch; i < data.length; i++) {
      ch = data.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 =
      Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^
      Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 =
      Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^
      Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (
      (h2 >>> 0).toString(16).padStart(8, "0") +
      (h1 >>> 0).toString(16).padStart(8, "0")
    );
  } catch (e) {
    return "anonymous-" + Math.random().toString(36).substring(2);
  }
};

let cachedFingerprint = null;

// Fallback storage for CSRF token when cookies are restricted (cross-domain)
let fallbackCsrfToken = localStorage.getItem("x-csrf-token-fallback");

/**
 * Helper to retrieve a cookie value by name.
 */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
};

const getFingerprint = () => {
  if (!cachedFingerprint) {
    cachedFingerprint = localStorage.getItem("x-device-fingerprint");
    if (!cachedFingerprint) {
      cachedFingerprint = getDeviceFingerprint();
      localStorage.setItem("x-device-fingerprint", cachedFingerprint);
    }
  }
  return cachedFingerprint;
};

// Ensure fingerprint is generated on load
getFingerprint();

// API url
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001/api/v1";

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;

    // Mandatory Security Headers for Anti-Replay & Zero Trust Context
    config.headers["x-request-id"] =
      window.crypto?.randomUUID?.() ||
      Date.now().toString(36) + Math.random().toString(36).substring(2);
    config.headers["x-timestamp"] = Date.now().toString();
    config.headers["x-device-fingerprint"] = getFingerprint();

    // CSRF Protection: Double-Submit Cookie Pattern
    // Proves the request came from our frontend by mirroring the cookie into a header
    const csrfToken = getCookie("x-csrf-token-secret") || fallbackCsrfToken;
    if (csrfToken) config.headers["x-csrf-token"] = csrfToken;

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    // Capture CSRF token from headers if provided by the server (essential for cross-domain)
    const csrfHeader = response.headers["x-csrf-token"];
    if (csrfHeader) {
      fallbackCsrfToken = csrfHeader;
      localStorage.setItem("x-csrf-token-fallback", csrfHeader);
    }
    return response;
  },
  (error) => {
    // Zero Trust: Handle re-authentication challenge for sensitive actions (Sudo Mode)
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === "REAUTH_REQUIRED"
    ) {
      // Notify the application that sudo re-verification is needed.
      // The UI (e.g., App.jsx or a Layout component) should listen for this event to show ReauthModal.
      window.dispatchEvent(
        new CustomEvent("sudo-reauth-required", {
          detail: {
            message: error.response.data.message,
            config: error.config,
          },
        }),
      );
    }
    return Promise.reject(error);
  },
);

export { api };
